/** @jsx jsx */
import { css, jsx } from '@emotion/react';

import * as React from 'react';
import ReactModal from 'react-modal';

import connectToRouter from 'js/lib/connectToRouter';

import type { Theme } from '@coursera/cds-core';
import { Button, Grid, PageGridContainer, breakpoints } from '@coursera/cds-core';
import { ArrowPreviousIcon } from '@coursera/cds-icons';
import { TunnelVision } from '@coursera/coursera-ui';

import LeaveConfirmModal from 'bundles/compound-assessments/components/modals/LeaveConfirmModal';

import _t from 'i18n!nls/compound-assessments';

type Props = {
  onClose: () => void;
  shouldConfirmClose?: boolean;
  headerLeft: React.ReactNode;
  headerRight: React.ReactNode;
  topBanner?: React.ReactNode;
  children: React.ReactNode;
  backbuttonAriaLabel?: string;
  ariaLabel?: string;
};

type State = {
  isOpen: boolean;
  showLeaveConfirmModal: boolean;
  storedClose?: (() => void) | null;
};

const ARROW_UP_KEY = 38;
const ARROW_DOWN_KEY = 40;
const PAGE_UP_KEY = 33;
const PAGE_DOWN_KEY = 34;
const HOME_KEY = 36;
const END_KEY = 35;

const styles = {
  headerRight: (theme: Theme) =>
    css({
      display: 'flex',
      [breakpoints.up('md')]: {
        justifyContent: 'center',
      },
      [breakpoints.down('xs')]: {
        justifyContent: 'left',
        marginTop: 'var(--cds-spacing-100)',
        marginBottom: 'var(--cds-spacing-50)',
      },
      alignItems: 'center',
    }),
  headerLeft: (theme: Theme) =>
    css({
      [breakpoints.down('xs')]: {
        marginTop: 'var(--cds-spacing-200)',
      },
    }),
  tunnelVisionContentContainer: css({
    overflow: 'scroll',
    width: '100%',
  }),
  closeElementContainer: css({
    display: 'flex',
    alignItems: 'center',
  }),
  closeElementBackText: (theme: Theme) =>
    css({
      marginLeft: 'var(--cds-spacing-100)',
      color: 'var(--cds-color-blue-700)',
    }),
  tunnelVision: (theme: Theme) =>
    css({
      // Override coursera-ui styles
      '.rc-TunnelVision div[data-classname="tunnelvision-window-0"]': {
        [breakpoints.down('sm')]: {
          marginLeft: 0,
        },
      },

      '.rc-TunnelVisionClose': {
        backgroundColor: 'var(--cds-color-white-0)',
        ':hover': {
          backgroundColor: 'var(--cds-color-blue-25)',
        },
        [breakpoints.up('md')]: {
          marginLeft: 'var(--cds-spacing-600)',
        },
        [breakpoints.down('sm')]: {
          marginLeft: 'var(--cds-spacing-200)',
        },
      },
    }),
};

export const TUNNELVISIONWRAPPER_CONTENT_ID = 'TUNNELVISIONWRAPPER_CONTENT_ID';

export class TunnelVisionWrapper extends React.Component<Props, State> {
  state: State = {
    isOpen: false,
    showLeaveConfirmModal: false,
    storedClose: null,
  };

  contentRef: HTMLElement | null = null;

  componentDidMount() {
    // Need to call ReactModal.setAppElement on CSR when DOM is ready before opening ReactModal
    ReactModal.setAppElement('#rendered-content');
    this.setState({
      isOpen: true,
    });
    document.addEventListener('keydown', this.handleKeyDown);
  }

  componentWillUnmount() {
    document.removeEventListener('keydown', this.handleKeyDown);
  }

  handleKeyDown = (e: KeyboardEvent): void => {
    const { contentRef, scrollPage } = this;
    if (contentRef) {
      const currentPosition = contentRef.scrollTop;
      switch (e.keyCode) {
        case ARROW_UP_KEY: {
          if (currentPosition <= 0) {
            return;
          }
          scrollPage(currentPosition - 50);
          break;
        }
        case PAGE_UP_KEY: {
          if (currentPosition <= 0) {
            return;
          }
          scrollPage(currentPosition - contentRef.clientHeight);
          break;
        }
        case HOME_KEY: {
          scrollPage(0);
          break;
        }
        case ARROW_DOWN_KEY: {
          if (currentPosition >= contentRef.scrollHeight) {
            return;
          }
          scrollPage(currentPosition + 50);
          break;
        }
        case PAGE_DOWN_KEY: {
          if (currentPosition >= contentRef.scrollHeight) {
            return;
          }
          scrollPage(currentPosition + contentRef.clientHeight);
          break;
        }
        case END_KEY: {
          scrollPage(contentRef.scrollHeight);
          break;
        }
        default:
          break;
      }
    }
  };

  handleLeaveConfirm = () => {
    const { storedClose } = this.state;

    this.setState({ showLeaveConfirmModal: false, storedClose: null });
    if (storedClose) {
      storedClose();
    }
  };

  handleLeaveCancel = () => {
    this.setState({ showLeaveConfirmModal: false, storedClose: null });
  };

  scrollPage = (newPosition: $TSFixMe) => {
    const { contentRef } = this;
    if (contentRef) {
      contentRef.scrollTo(0, newPosition);
    }
  };

  shouldAllowClose = (close?: () => void): boolean => {
    const { shouldConfirmClose } = this.props;

    if (shouldConfirmClose) {
      this.setState({ showLeaveConfirmModal: true, storedClose: close });
      return false;
    }
    return true;
  };

  render() {
    const { onClose, headerLeft, headerRight, topBanner, children, ariaLabel, backbuttonAriaLabel } = this.props;
    const { isOpen, showLeaveConfirmModal } = this.state;
    return (
      <ReactModal
        // style required so the tunnelvision is above the page header
        style={{
          overlay: {
            position: 'relative',
            zIndex: 4000,
          },
        }}
        isOpen={isOpen}
        shouldCloseOnEsc={false}
        shouldCloseOnOverlayClick={false}
        css={styles.tunnelVision}
      >
        <TunnelVision
          disableKeyPress={true}
          onClose={onClose}
          // note: refer to CUI source code for comments on how this feature functions
          shouldAllowClose={this.shouldAllowClose}
          ariaLabel={ariaLabel}
          backbuttonAriaLabel={backbuttonAriaLabel}
          closeElement={
            <Button variant="ghost" icon={<ArrowPreviousIcon />} iconPosition="before" component="div" tabIndex={-1}>
              {_t('Back')}
            </Button>
          }
          headerElement={
            <Grid
              container
              justifyContent="space-between"
              alignItems="center"
              css={(theme: Theme) =>
                css({
                  marginLeft: 'var(--cds-spacing-200)',
                })
              }
              data-testid="TunnelVisionWrapper__header"
            >
              <Grid item sm={9} lg={10} xs={12} css={styles.headerLeft} data-testid="TunnelVisionWrapper__header-left">
                {headerLeft}
              </Grid>
              <Grid
                item
                sm={3}
                lg={2}
                xs={12}
                justifyContent="flex-start"
                css={styles.headerRight}
                data-testid="TunnelVisionWrapper__header-right"
              >
                {headerRight}
              </Grid>
            </Grid>
          }
        >
          <div css={styles.tunnelVisionContentContainer} id={TUNNELVISIONWRAPPER_CONTENT_ID}>
            {topBanner && (
              <div role="alert" aria-live="assertive">
                {topBanner}
              </div>
            )}
            <PageGridContainer>
              <Grid item container justifyContent="center">
                <Grid
                  item
                  lg={8}
                  xs={12}
                  css={(theme: Theme) =>
                    css({
                      marginTop: 'var(--cds-spacing-400)',
                    })
                  }
                >
                  <div
                    ref={(ref) => {
                      this.contentRef = ref;
                    }}
                  >
                    <div>{children}</div>
                  </div>
                </Grid>
              </Grid>
            </PageGridContainer>
          </div>

          {showLeaveConfirmModal && (
            <LeaveConfirmModal
              onPrimaryButtonClick={this.handleLeaveConfirm}
              onCancelButtonClick={this.handleLeaveCancel}
            />
          )}
        </TunnelVision>
      </ReactModal>
    );
  }
}

export const withRouterOnClose = connectToRouter<
  Props,
  Omit<Props, 'onClose'> & {
    onCloseRouteName: string;
  }
>((router, { onCloseRouteName }) => ({
  onClose: () => {
    router.push({
      name: onCloseRouteName,
      params: router.params,
      query: router.location.query,
    });
  },
}));

export default withRouterOnClose(TunnelVisionWrapper);
